import { useInfiniteQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { TimelineResponse } from '@/types/timeline'

const PAGE_SIZE = 10

/**
 * Timeline d'un contact (calls + SMS + voicemails + notes), paginée.
 *
 * Backend trie anti-chrono ; chaque page apporte les 10 plus anciens encore non
 * chargés. Le rendu inverse l'ordre côté UI pour afficher chrono.
 * `line` : seulement les échanges passés sur cette ligne de l'entreprise.
 * `until` : conversation opened on a search result; the first page reaches
 * back to that date, so the exchange is loaded (and stays loaded on refetch).
 */
export function useContactTimeline(phoneNumber: string | null, line: string | null = null, until: string | null = null) {
  return useInfiniteQuery({
    queryKey: ['contact-timeline', phoneNumber, line, until],
    queryFn: async ({ pageParam = 0 }) => {
      if (!phoneNumber) {
        return { events: [], has_more: false, total: 0 } satisfies TimelineResponse
      }
      const { data } = await api.get<TimelineResponse>(
        `/api/contacts/${encodeURIComponent(phoneNumber)}/timeline/`,
        {
          params: {
            limit: PAGE_SIZE,
            offset: pageParam,
            line: line || undefined,
            until: pageParam === 0 && until ? until : undefined,
          },
        },
      )
      return data
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined
      return allPages.reduce((sum, p) => sum + p.events.length, 0)
    },
    enabled: !!phoneNumber,
  })
}
